import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// FIX SUMMARY:
// 1. Bubble count reduced from 24 → 16. O(n²) collision = 24²=576 checks/frame vs 16²=256.
// 2. Collision detection now runs every OTHER frame (collisionTickRef) — halves CPU cost.
// 3. Canvas gets `will-change: transform` so browser promotes it to its own compositor layer.
// 4. `image-rendering: pixelated` removed — it caused extra compositing work.

const InteractiveServices = () => {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const bubblesRef = useRef([]);
  const animationRef = useRef(null);
  const draggedBubbleRef = useRef(null);
  const hasDroppedRef = useRef(false);
  const dropTimeoutsRef = useRef([]);
  const collisionTickRef = useRef(0); // NEW: frame counter for throttling

  const services = [
    'Custom Embroidery',
    'Logo Digitizing',
    'Cap Digitizing',
    '3D Puff',
    'Flat Embroidery',
    'Left Chest',
    'Jacket Back',
    'Sleeve Logo',
    'Applique',
    'Chenille',
    'Patch',
    'Badge',
    'Monogram',
    'Vector Art',
    'DTF Printing',
    'Custom Patches',
  ]; // exactly 16

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    class Bubble {
      constructor(text, x, y, shouldDrop = false) {
        this.text = text;
        this.targetX = x;
        this.targetY = y;
        this.x = shouldDrop ? x + (Math.random() - 0.5) * 100 : x;
        this.y = shouldDrop ? -200 : y;
        this.vx = shouldDrop ? (Math.random() - 0.5) * 3 : 0;
        this.vy = 0;
        this.radius = 55;
        this.mass = this.radius / 10;
        this.hovered = false;
        this.isDragged = false;
        this.isLocked = false;
        this.gravity = 0.5;
        this.hasLanded = false;
        this.isActive = !shouldDrop;
      }

      draw(ctx) {
        if (!this.isActive) return;

        const gradient = ctx.createRadialGradient(
          this.x - this.radius * 0.3,
          this.y - this.radius * 0.3,
          0,
          this.x,
          this.y,
          this.radius
        );

        if (this.hovered || this.isDragged) {
          gradient.addColorStop(0, '#049de2');
          gradient.addColorStop(1, '#002536');
        } else {
          gradient.addColorStop(0, '#ffffff');
          gradient.addColorStop(1, '#e8f4f8');
        }

        if (this.isDragged) {
          ctx.shadowColor = '#049de2';
          ctx.shadowBlur = 20;
        }

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.strokeStyle = (this.hovered || this.isDragged) ? '#ffffff' : '#002536';
        ctx.lineWidth = (this.hovered || this.isDragged) ? 3 : 2;
        ctx.stroke();

        ctx.shadowBlur = 0;

        ctx.fillStyle = (this.hovered || this.isDragged) ? '#ffffff' : '#002536';
        ctx.font = `bold ${this.radius / 4}px 'Bai Jamjuree', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const words = this.text.split(' ');
        const lineHeight = this.radius / 3.5;
        let line = '';
        let lines = [];

        words.forEach(word => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > this.radius * 1.5) {
            lines.push(line);
            line = word + ' ';
          } else {
            line = testLine;
          }
        });
        lines.push(line);

        const startY = this.y - ((lines.length - 1) * lineHeight) / 2;
        lines.forEach((line, i) => {
          ctx.fillText(line.trim(), this.x, startY + i * lineHeight);
        });
      }

      update(canvas, mouse) {
        if (!this.isActive) return;

        if (!this.isLocked) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          this.hovered = distance < this.radius;
        }

        if (this.isDragged && this.isLocked) {
          const prevX = this.x;
          const prevY = this.y;
          this.x = mouse.x;
          this.y = mouse.y;
          this.vx = this.x - prevX;
          this.vy = this.y - prevY;
        } else {
          this.vy += this.gravity;
          this.x += this.vx;
          this.y += this.vy;
          this.vx *= 0.95;
          this.vy *= 0.95;
        }

        if (!this.isDragged) {
          if (this.x - this.radius < 0) { this.x = this.radius; this.vx *= -0.6; }
          if (this.x + this.radius > canvas.width) { this.x = canvas.width - this.radius; this.vx *= -0.6; }
          if (this.y - this.radius < 0) { this.y = this.radius; this.vy *= -0.6; }
          if (this.y + this.radius > canvas.height) {
            this.y = canvas.height - this.radius;
            this.vy *= -0.7;
            this.hasLanded = true;
          }
        }
      }

      checkCollision(other) {
        if (!this.isActive || !other.isActive) return;

        const dx = other.x - this.x;
        const dy = other.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minDistance = this.radius + other.radius;

        if (distance < minDistance) {
          const angle = Math.atan2(dy, dx);
          const sine = Math.sin(angle);
          const cosine = Math.cos(angle);

          const vx1 = this.vx * cosine + this.vy * sine;
          const vy1 = this.vy * cosine - this.vx * sine;
          const vx2 = other.vx * cosine + other.vy * sine;
          const vy2 = other.vy * cosine - other.vx * sine;

          const totalMass = this.mass + other.mass;
          const vx1Final = ((this.mass - other.mass) * vx1 + 2 * other.mass * vx2) / totalMass;
          const vx2Final = ((other.mass - this.mass) * vx2 + 2 * this.mass * vx1) / totalMass;

          if (!this.isDragged) {
            this.vx = vx1Final * cosine - vy1 * sine;
            this.vy = vy1 * cosine + vx1Final * sine;
          }
          if (!other.isDragged) {
            other.vx = vx2Final * cosine - vy2 * sine;
            other.vy = vy2 * cosine + vx2Final * sine;
          }

          const overlap = minDistance - distance;
          const separateX = (overlap / 2) * Math.cos(angle);
          const separateY = (overlap / 2) * Math.sin(angle);

          if (!this.isDragged) { this.x -= separateX; this.y -= separateY; }
          if (!other.isDragged) { other.x += separateX; other.y += separateY; }

          if (this.isDragged && !other.isDragged) {
            other.vx += Math.cos(angle) * 3;
            other.vy += Math.sin(angle) * 3;
          } else if (other.isDragged && !this.isDragged) {
            this.vx -= Math.cos(angle) * 3;
            this.vy -= Math.sin(angle) * 3;
          }
        }
      }

      activate() { this.isActive = true; }
    }

    const initBubbles = (shouldDrop) => {
      bubblesRef.current = [];
      const bubbleRadius = 55;
      const spacing = bubbleRadius * 2 + 10;
      const bubblesPerRow = Math.floor(canvas.width / spacing);

      services.forEach((service, index) => {
        const row = Math.floor(index / bubblesPerRow);
        const col = index % bubblesPerRow;
        const x = spacing / 2 + col * spacing + (Math.random() - 0.5) * 40;
        const y = canvas.height - bubbleRadius - (row * spacing);
        bubblesRef.current.push(new Bubble(service, x, y, shouldDrop));
      });

      bubblesRef.current.sort(() => Math.random() - 0.5);
      if (shouldDrop) dropBubblesInWaves();
    };

    const dropBubblesInWaves = () => {
      dropTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      dropTimeoutsRef.current = [];

      let currentIndex = 0;
      const totalBubbles = bubblesRef.current.length;

      const dropWave = () => {
        if (currentIndex >= totalBubbles) return;
        const waveSize = Math.random() > 0.5 ? 3 : 2;
        for (let i = 0; i < waveSize && currentIndex < totalBubbles; i++) {
          bubblesRef.current[currentIndex]?.activate();
          currentIndex++;
        }
        if (currentIndex < totalBubbles) {
          const t = setTimeout(dropWave, 400 + Math.random() * 200);
          dropTimeoutsRef.current.push(t);
        }
      };

      dropWave();
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      if (draggedBubbleRef.current) return;

      let foundHover = false;
      bubblesRef.current.forEach(bubble => {
        if (!bubble.isActive) return;
        const dx = mouseRef.current.x - bubble.x;
        const dy = mouseRef.current.y - bubble.y;
        if (Math.sqrt(dx * dx + dy * dy) < bubble.radius && !foundHover) {
          bubble.isDragged = true;
          bubble.isLocked = true;
          draggedBubbleRef.current = bubble;
          foundHover = true;
          canvas.style.cursor = 'grabbing';
        }
      });
    };

    const handleMouseLeave = () => {
      if (draggedBubbleRef.current) {
        draggedBubbleRef.current.isDragged = false;
        draggedBubbleRef.current.isLocked = false;
        draggedBubbleRef.current = null;
      }
      bubblesRef.current.forEach(b => { b.isDragged = false; b.isLocked = false; });
      canvas.style.cursor = 'pointer';
    };

    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      bubblesRef.current.forEach(bubble => {
        if (!bubble.isActive) return;
        const dx = mouseX - bubble.x;
        const dy = mouseY - bubble.y;
        if (Math.sqrt(dx * dx + dy * dy) < bubble.radius) {
          bubble.isDragged = true;
          bubble.isLocked = true;
          draggedBubbleRef.current = bubble;
          canvas.style.cursor = 'grabbing';
        }
      });
    };

    const handleMouseUp = () => {
      if (draggedBubbleRef.current) {
        draggedBubbleRef.current.isDragged = false;
        draggedBubbleRef.current.isLocked = false;
        draggedBubbleRef.current = null;
      }
      bubblesRef.current.forEach(b => { b.isDragged = false; b.isLocked = false; });
      canvas.style.cursor = 'pointer';
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      collisionTickRef.current++;
      const runCollision = collisionTickRef.current % 2 === 0; // FIX: collision every other frame

      bubblesRef.current.forEach((bubble, i) => {
        bubble.update(canvas, mouseRef.current);

        if (runCollision) {
          for (let j = i + 1; j < bubblesRef.current.length; j++) {
            bubble.checkCollision(bubblesRef.current[j]);
          }
        }

        bubble.draw(ctx);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();

    ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top 60%',
      onEnter: () => {
        if (!hasDroppedRef.current) {
          hasDroppedRef.current = true;
          initBubbles(true);
        }
      }
    });

    animate();

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', resizeCanvas);
      dropTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section ref={sectionRef} className="interactive-services-section">
      <div className="container-fluid">
        <div className="row">
          <div className="canvas-wrapper">
            {/* FIX: will-change:transform promotes canvas to its own GPU layer */}
            <canvas ref={canvasRef} className="interactive-canvas" style={{ willChange: 'transform' }}></canvas>
            <div className="canvas-bottom-border">
              <div className="border-gradient"></div>
              <div className="border-dots">
                {[...Array(10)].map((_, i) => <span key={i}></span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveServices;